/**
 * useCloudSync.js
 *
 * React hook that encapsulates the entire WebAuthn + Cloud Sync lifecycle.
 *
 * Exposed interface:
 *   isSynced        – true when quran_auth_token is present in localStorage
 *   isLoading       – true while a WebAuthn ceremony or network request is in flight
 *   error           – string | null — most recent user-visible error message
 *   lastSynced      – Date | null  — parsed from the last successful pull/push
 *   enableSync()    – triggers WebAuthn registration → stores token → pulls data
 *   loginSync()     – triggers WebAuthn authentication → stores token → pulls data
 *   disableSync()   – clears token and local sync state
 *   manualSync()    – triggers an immediate SyncService push + pull cycle
 *
 * The hook chooses register vs. authenticate automatically:
 *   - If no token exists  → offer registration (first device) AND login (existing device)
 *   - Callers can force one path by calling enableSync() or loginSync() directly.
 */

import { useState, useEffect, useCallback } from 'react';
import {
    startRegistration,
    startAuthentication,
} from '@simplewebauthn/browser';
import SyncService from '../services/SyncService';

const TOKEN_KEY = 'quran_auth_token';
const USERID_KEY = 'quran_user_id';
const LAST_SYNCED_KEY = 'quran_last_synced';

export function useCloudSync() {
    const [isSynced, setIsSynced] = useState(() => !!localStorage.getItem(TOKEN_KEY));
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [lastSynced, setLastSynced] = useState(() => {
        const stored = localStorage.getItem(LAST_SYNCED_KEY);
        return stored ? new Date(stored) : null;
    });

    // Keep isSynced in sync with storage (e.g. another tab logs out)
    useEffect(() => {
        const onStorage = (e) => {
            if (e.key === TOKEN_KEY) {
                setIsSynced(!!e.newValue);
            }
        };
        window.addEventListener('storage', onStorage);
        return () => window.removeEventListener('storage', onStorage);
    }, []);

    const clearError = useCallback(() => setError(null), []);

    // ── Shared: store token and trigger initial pull ─────────────────────────
    const _onAuthSuccess = useCallback(async (token, userId) => {
        localStorage.setItem(TOKEN_KEY, token);
        if (userId) localStorage.setItem(USERID_KEY, userId);
        setIsSynced(true);

        // Pull remote data immediately so all devices show the same state
        const pulled = await SyncService.pull();
        if (pulled) {
            const now = new Date();
            localStorage.setItem(LAST_SYNCED_KEY, now.toISOString());
            setLastSynced(now);
            // SyncService.pull() reloads the page on meaningful changes,
            // so we only reach here when nothing changed.
        }
    }, []);

    // ── Registration (first-time / new device) ───────────────────────────────
    const enableSync = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            // 1. Get options from server
            const optRes = await fetch('/api/auth/register-options', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({}),
            });
            if (!optRes.ok) throw new Error('Server unreachable. Please try again.');
            const { challengeId, userId, options } = await optRes.json();

            // 2. Trigger native biometric prompt
            let registrationResponse;
            try {
                registrationResponse = await startRegistration({ optionsJSON: options });
            } catch (err) {
                // User cancelled or device doesn't support biometrics
                if (err.name === 'NotAllowedError') {
                    throw new Error('Biometric prompt was cancelled or timed out.');
                }
                throw new Error('Passkey creation failed: ' + err.message);
            }

            // 3. Verify with server
            const verifyRes = await fetch('/api/auth/verify-registration', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ challengeId, userId, registrationResponse }),
            });
            if (!verifyRes.ok) {
                const body = await verifyRes.json().catch(() => ({}));
                throw new Error(body.error || 'Verification failed. Please try again.');
            }
            const { token } = await verifyRes.json();

            await _onAuthSuccess(token, userId);
        } catch (err) {
            setError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [_onAuthSuccess]);

    // ── Authentication (returning user on existing / new device) ─────────────
    const loginSync = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            // 1. Get options — pass stored userId if available so the browser can
            //    pre-select the right credential
            const storedUserId = localStorage.getItem(USERID_KEY);
            const optRes = await fetch('/api/auth/login-options', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(storedUserId ? { userId: storedUserId } : {}),
            });
            if (!optRes.ok) throw new Error('Server unreachable. Please try again.');
            const { challengeId, options } = await optRes.json();

            // 2. Trigger native biometric prompt
            let authenticationResponse;
            try {
                authenticationResponse = await startAuthentication({ optionsJSON: options });
            } catch (err) {
                if (err.name === 'NotAllowedError') {
                    throw new Error('Biometric prompt was cancelled or timed out.');
                }
                throw new Error('Passkey sign-in failed: ' + err.message);
            }

            // 3. Verify with server
            const verifyRes = await fetch('/api/auth/verify-login', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ challengeId, authenticationResponse }),
            });
            if (!verifyRes.ok) {
                const body = await verifyRes.json().catch(() => ({}));
                throw new Error(body.error || 'Sign-in failed. Please try again.');
            }
            const { token } = await verifyRes.json();

            await _onAuthSuccess(token, storedUserId);
        } catch (err) {
            setError(err.message || 'Something went wrong. Please try again.');
        } finally {
            setIsLoading(false);
        }
    }, [_onAuthSuccess]);

    // ── Sign out ─────────────────────────────────────────────────────────────
    const disableSync = useCallback(() => {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.removeItem(USERID_KEY);
        localStorage.removeItem(LAST_SYNCED_KEY);
        setIsSynced(false);
        setLastSynced(null);
        setError(null);
    }, []);

    // ── Manual sync (push current state immediately) ─────────────────────────
    const manualSync = useCallback(async () => {
        if (!isSynced) return;
        setIsLoading(true);
        setError(null);
        try {
            // Force immediate push (bypasses debounce)
            await SyncService._push();
            const now = new Date();
            localStorage.setItem(LAST_SYNCED_KEY, now.toISOString());
            setLastSynced(now);
        } catch (err) {
            setError('Sync failed. Check your connection and try again.');
        } finally {
            setIsLoading(false);
        }
    }, [isSynced]);

    return {
        isSynced,
        isLoading,
        error,
        lastSynced,
        clearError,
        enableSync,
        loginSync,
        disableSync,
        manualSync,
    };
}
